import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiDataService } from 'src/app/api/services/api-data.service';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { CecBreakpointPipe } from 'src/app/controls/pipes/cec-breakpoint.pipe';
import { GridComponent } from 'src/app/controls/grid/components/grid.component';
import { GRID_COLUMN_DEFINITION } from '../constants/grid-column-definition.constant';
import { RegisteredCrewsTableService } from '../services/registered-crews-table.service';
import { MatDialog } from '@angular/material/dialog';
import { ToolbarComponent } from 'src/app/controls/toolbar/components/toolbar.component';
import { ToolbarItemDirective } from 'src/app/controls/toolbar/directives/toolbar-item.directive';
import { DialogComponent } from 'src/app/controls/dialog/components/dialog.component';
import { CompetitionRegistrationComponent } from '../../competition-registration/components/competition-registration.component';
import { SnackBarService } from 'src/app/controls/snack-bar/services/snack-bar.service';
import { GridButtonTrigger } from 'src/app/controls/grid/models/grid-button-trigger.interface';
import { RegisteredCrewsTable } from '../models/registered-crews-table.interface';
import { ConfirmationDialogComponent } from 'src/app/controls/confirmation-dialog/confirmation-dialog.component';
import { InfoDialogComponent } from 'src/app/controls/info-dialog/info-dialog.component';
import { SelectedItem } from 'src/app/controls/models/selected-item.interface';
import { BreadcrumbComponent } from 'src/app/controls/breadcrumb/components/breadcrumb.component';
import { GridBase } from 'src/app/controls/base/grid.base';
import { CompetitionRegistrationService } from '../../competition-registration/services/competition-registration.service';

@Component({
  selector: 'app-registered-crews-table',
  templateUrl: './registered-crews-table.component.html',
  styleUrls: ['./registered-crews-table.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatCardModule,
    MatTableModule,
    MatIconModule,
    TranslateModule,
    MatTooltipModule,
    DatePipe,
    CecBreakpointPipe,
    CommonModule,
    GridComponent,
    ToolbarComponent,
    ToolbarItemDirective,
    CompetitionRegistrationComponent,
    ConfirmationDialogComponent,
    InfoDialogComponent,
    BreadcrumbComponent
  ],
  providers: [RegisteredCrewsTableService],
})
export class RegisteredCrewsTableComponent extends GridBase {

  competitionId?: number;

  service: RegisteredCrewsTableService = inject(RegisteredCrewsTableService);

  competitionRegistrationService = inject(CompetitionRegistrationService);

  @ViewChild('registrationDialog') registrationDialog!: CompetitionRegistrationComponent;

  protected infoDialogTextAreaLabelKey: string | undefined = undefined;

  constructor(
    private apiService: ApiDataService
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.competitionId = this.route.parent?.snapshot.params.id as number;

    this.sub.add(
      this.competitionRegistrationService.reload.subscribe(() => {
        this.reload();
      })
    );
  }

  /**
   * function for navigate to competition details
   */
  navigateToCompetitionDetails(): void {
    this.router.navigate(['/competition', this.competitionId, 'edit']);
  }

  printRegistrations(): void {
    const printRegistrations =
      'competition-registrations/print-competitors-info/' + this.competitionId;
    this.apiService.downloadFile(printRegistrations).subscribe();
  }

  protected onButtonClick(event: GridButtonTrigger<RegisteredCrewsTable>): void {
    switch (event.buttonId) {
      case 'additionalCompetitors':
        this.infoDialog.showList(event.row.additionalCompetitors);
        break;
      case 'noteCompetitor':
        this.infoDialogTextAreaLabelKey = 'Competitor note';
        this.infoDialog.show(event.row.noteCompetitor);
        break;
      case 'noteAuthor':
        this.infoDialogTextAreaLabelKey = 'Author note';
        this.infoDialog.show(event.row.noteAuthor, event.row.id, this.isOrganizer());
        break;
      case 'edit':
        this.registrationDialog.show(this.competitionId, this.isOrganizer(), event.row.id);
        break;
      case 'logout':
        this.confirmationDialog.show(event.row.id);
        break;
    }
  }

  protected onToolbarClick(itemId: string): void {
    switch (itemId) {
      case 'add':
        this.registrationDialog.show(this.competitionId, this.isOrganizer());
        break;
      case 'print':
        this.print();
        break;
    }
  }

  /**
   * Unregisters the desired crew from competition
   */
  protected onLogoutButtonClick(id: number): void {
    /** if id is not provided, do nothing */
    if (!id) {
      return;
    }

    /** if id was provided, remove desired competition registration */
    this.service.removeCrewFromCompetition(id).subscribe((result: boolean) => {
      /** if competition registration was removed, show success snackbar */
      if (result) {
        this.snackBar.success(this.translate.instant('CrewUnregistered'));
        this.grid.reload();
        return;
      }

      /** else show danger snackbar */
      this.snackBar.danger(this.translate.instant('UnableToUnregisterCrew'));
    });
  }

  /**
   * Updates the author note of competition registration
   * @param message the updated author note message
   */
  protected authorNoteUpdate(item: SelectedItem): void {
    /** if cancel button was clicked */
    if (item === undefined) {
      return;
    }

    /** updates the author note of competition registration */
    this.service.updateAuthorNote({
      id: item.value,
      noteAuthor: item.text
    }).subscribe((result: boolean) => {
      if (result) {
        /** shows success snackbar when author note was successfully updated */
        this.snackBar.success(this.translate.instant('AuthorNoteUpdated'));

        /** reloads registered crews table */
        this.reload();
        return;
      }

      /** shows danger snackbar when author note was not updated */
      this.snackBar.danger(this.translate.instant('AuthorNoteUpdateFailed'));
    })
  }
}
